// HEADER-DOCS-012: UploadFileModal
// Spec: §4.3, §5.5 (3-path retry), §15.3 (single file), §15.6 (keyboard a11y)
//
// Flow: initiateUpload (presigned PUT) -> S3 PUT -> create document row.
// Retry resumes from the step that failed: a failed create does not re-upload, a failed
// S3 PUT re-initiates (the old presigned slot is cancelled best-effort).

import React, { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

import {
  documentsApi,
  DocumentDto,
  DocType,
  DOC_TYPES,
  DOC_TYPE_LABELS,
  httpStatusOf,
  isApiError,
} from '../../../../services/camMirrorService';
import { DocFieldLabel, docFieldSx } from './DocFieldLabel';

interface UploadFileModalProps {
  studentId: number;
  defaultDocType?: DocType;
  onClose: () => void;
  onComplete: (doc: DocumentDto) => void;
}

const MAX_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB, same as backend MAX_UPLOAD_BYTES
// Must match backend ALLOWED_MIME_TYPES (cam-attachments.service.ts, spec §4.3).
// HEADER-DOCS-026 follow-up: + PPT/PPTX, TXT, CSV, ODT/ODS/ODP, WEBP. Archives stay blocked.
const ACCEPTED_MIME = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-powerpoint',
  'text/plain',
  'text/csv',
  'application/vnd.oasis.opendocument.text',
  'application/vnd.oasis.opendocument.spreadsheet',
  'application/vnd.oasis.opendocument.presentation',
  'image/png',
  'image/jpeg',
  'image/heic',
  'image/heif',
  'image/webp',
  'image/gif', // HEADER-DOCS-025 Q2: gif allowed in chat, Documents and CAM
];
// Extensions are listed too: browsers often report '' / octet-stream / a wrong MIME
// (e.g. .csv as application/vnd.ms-excel), and the backend maps those by extension.
const ACCEPTED_EXTENSIONS = [
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv',
  'odt', 'ods', 'odp', 'png', 'jpg', 'jpeg', 'heic', 'heif', 'webp', 'gif',
];
const ACCEPT_ATTR = [...ACCEPTED_MIME, ...ACCEPTED_EXTENSIONS.map((ext) => `.${ext}`)].join(',');
const SUPPORTED_LABEL = 'PDF, Word, Excel, PowerPoint, TXT, CSV, LibreOffice (ODT/ODS/ODP), PNG, JPG, GIF, HEIC, WEBP';
// MIME values the backend resolves from the extension (normalizeDocumentMime).
const GENERIC_MIME = ['', 'application/octet-stream', 'binary/octet-stream', 'application/zip', 'application/x-zip-compressed'];

function fileExtension(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
}

type Step = 'idle' | 'initiating' | 'uploading' | 'creating' | 'error';

const STEP_LABEL: Record<Step, string> = {
  idle: '',
  initiating: 'Preparing upload…',
  uploading: 'Uploading…',
  creating: 'Saving document…',
  error: '',
};

// Returns an error message, or null when the file is acceptable.
function validateFile(f: File): string | null {
  if (f.size > MAX_SIZE_BYTES) return 'File is too large. Max 10 MB.';
  // A known extension decides when the browser MIME is generic or merely an alias;
  // a specific foreign MIME (e.g. an executable renamed to .txt) is still rejected by the backend.
  const ext = fileExtension(f.name);
  const extOk = ACCEPTED_EXTENSIONS.includes(ext);
  const type = (f.type || '').toLowerCase();
  // .csv is accepted with any browser MIME here (Excel reports application/vnd.ms-excel,
  // some browsers text/plain); the backend still decides via normalizeDocumentMime.
  // HEADER-DOCS-025 Q2: the extension must be whitelisted too, so notes.md / data.json
  // reported as text/plain are rejected (same rule as backend normalizeDocumentMime).
  const mimeOk = extOk && (ACCEPTED_MIME.includes(type) || GENERIC_MIME.includes(type) || ext === 'csv');
  if (!mimeOk) return `Unsupported file type. Supported: ${SUPPORTED_LABEL}.`;
  return null;
}

export const UploadFileModal: React.FC<UploadFileModalProps> = ({
  studentId,
  defaultDocType = 'transcript',
  onClose,
  onComplete,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [docType, setDocType] = useState<DocType>(defaultDocType);
  const [progress, setProgress] = useState(0);
  const [step, setStep] = useState<Step>('idle');
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  // Initiated attachment (needed for cancel) and whether its S3 PUT already succeeded (needed for retry).
  const [attachmentExternalId, setAttachmentExternalId] = useState<string | null>(null);
  const [s3Done, setS3Done] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploading = step === 'initiating' || step === 'uploading' || step === 'creating';

  const resetUploadState = () => {
    setAttachmentExternalId(null);
    setS3Done(false);
    setProgress(0);
    setStep('idle');
  };

  // ---------------------------------------------------------------------------
  // File picker + drag-drop (§15.3: single file only)
  // ---------------------------------------------------------------------------
  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    if (files.length > 1) {
      toast('Only one file at a time. Upload others separately.');
    }
    const first = files[0];
    const problem = validateFile(first);
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);
    setFile(first);
    resetUploadState();
    if (!displayName) setDisplayName(first.name.slice(0, 200));
  };

  const cancelInitiated = async (attId: string) => {
    try {
      await documentsApi.cancelUpload(attId);
    } catch {
      // Best effort: the orphan cron (HEADER-DOCS-019) cleans up anything left behind.
    }
  };

  // ---------------------------------------------------------------------------
  // Upload flow (initiate -> S3 PUT -> create row)
  // ---------------------------------------------------------------------------
  const onSubmit = async () => {
    if (!file || !displayName.trim() || uploading) return;
    setError(null);

    try {
      let attId = attachmentExternalId;

      if (!attId || !s3Done) {
        // A previous attempt initiated but the PUT failed: drop that slot and start over.
        if (attId) await cancelInitiated(attId);

        setStep('initiating');
        setProgress(0);
        const init = await documentsApi.initiateUpload({
          studentId,
          filename: file.name,
          mimeType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
          docType,
        });
        attId = init.attachmentExternalId;
        setAttachmentExternalId(attId);
        setS3Done(false);

        setStep('uploading');
        // Send the Content-Type the URL was signed with (normalized by the backend), not file.type
        await documentsApi.uploadToS3(init.uploadUrl, file, setProgress, init.contentType);
        setS3Done(true);
      }

      setStep('creating');
      const doc = await documentsApi.create({
        studentId,
        attachmentExternalId: attId,
        displayName: displayName.trim(),
        docType,
      });
      toast.success('File uploaded.');
      onComplete(doc);
    } catch (err) {
      setStep('error');
      const code = isApiError(err) ? err.response.data.code : undefined;
      if (code === 'financial_doc_blocked') {
        setError(
          'That looks like a financial document (FAFSA, tax return, etc.). Please share it through the school financial aid portal instead.'
        );
      } else if (code === 'unsupported_file_type') {
        setError(`Unsupported file type. Supported: ${SUPPORTED_LABEL}.`);
      } else if (code === 'file_too_large') {
        setError('File is too large. Max 10 MB.');
      } else if (httpStatusOf(err) === 429) {
        setError("You've uploaded a lot recently. Please wait a few minutes and try again.");
      } else if (code === 's3_unavailable' || code === 's3_not_configured') {
        setError('File storage is temporarily unavailable. Please try again shortly.');
      } else {
        setError('Upload failed. Please try again.');
      }
    }
  };

  const onCancel = async () => {
    if (uploading) return;
    // §4.3: initiated but no document row yet -> cancel the pending attachment.
    if (attachmentExternalId) await cancelInitiated(attachmentExternalId);
    onClose();
  };

  const openPicker = () => fileInputRef.current?.click();

  return (
    <Dialog open onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Upload file
        <IconButton onClick={onCancel} size="small" aria-label="Close" disabled={uploading}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {!file ? (
          <Box
            role="button"
            tabIndex={0}
            onClick={openPicker}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openPicker();
              }
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              handleFiles(e.dataTransfer.files);
            }}
            aria-label={`Upload one file. ${SUPPORTED_LABEL}. 10 megabytes maximum.`}
            sx={{
              p: 4,
              borderRadius: 2,
              border: '2px dashed',
              borderColor: dragOver ? 'primary.main' : 'divider',
              background: dragOver ? 'var(--gm-nested, #F7FBFD)' : 'transparent',
              textAlign: 'center',
              cursor: 'pointer',
              '&:hover': { background: 'action.hover' },
              '&:focus-visible': { outline: 'none', boxShadow: '0 0 0 3px rgba(62,115,240,0.35)' },
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            <Typography variant="body1" fontWeight={600} sx={{ mt: 1 }}>
              Drag one file here or click to browse
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {SUPPORTED_LABEL} (10 MB max)
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                size="small"
                tabIndex={-1}
                onClick={(e) => {
                  e.stopPropagation();
                  openPicker();
                }}
              >
                Browse files
              </Button>
            </Box>
            <input
              ref={fileInputRef}
              type="file"
              hidden
              accept={ACCEPT_ATTR}
              onChange={(e) => {
                handleFiles(e.target.files);
                // Allow re-selecting the same file after removing it.
                e.target.value = '';
              }}
            />
          </Box>
        ) : (
          // Usersnap #2757: 24px between fields, static labels above the inputs.
          <Stack spacing={3}>
            <Chip
              label={file.name}
              sx={{ alignSelf: 'flex-start', maxWidth: '100%' }}
              onDelete={
                !uploading
                  ? async () => {
                      if (attachmentExternalId) await cancelInitiated(attachmentExternalId);
                      setFile(null);
                      setDisplayName('');
                      setError(null);
                      resetUploadState();
                    }
                  : undefined
              }
            />
            <Box>
              <DocFieldLabel htmlFor="upload-display-name">Display name</DocFieldLabel>
              <TextField
                id="upload-display-name"
                fullWidth
                size="small"
                sx={docFieldSx}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value.slice(0, 200))}
                disabled={uploading}
              />
            </Box>
            <Box>
              <DocFieldLabel id="upload-doc-type-label">Document type</DocFieldLabel>
              <FormControl fullWidth size="small" sx={docFieldSx} disabled={uploading || s3Done}>
                <Select
                  labelId="upload-doc-type-label"
                  value={docType}
                  onChange={(e) => setDocType(e.target.value as DocType)}
                >
                  {DOC_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>
                      {DOC_TYPE_LABELS[t]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            {uploading && (
              <Box>
                <LinearProgress
                  variant={step === 'uploading' ? 'determinate' : 'indeterminate'}
                  value={progress}
                />
                <Typography variant="caption" color="text.secondary">
                  {step === 'uploading' ? `${progress}% uploaded` : STEP_LABEL[step]}
                </Typography>
              </Box>
            )}
          </Stack>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={uploading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={!file || !displayName.trim() || uploading}
        >
          {uploading ? <CircularProgress size={16} /> : step === 'error' ? 'Retry' : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
